import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Link, browserHistory } from 'react-router'
import { Card } from 'components'
import { LEARNING_HUB_ROUTE } from 'config'
import { courseCompletionPath } from '../../utils/block-navigation'
import { selectors as globalSelectors } from '../../global-reducer'
import * as apiActions from 'api-actions'
import * as flashActions from 'redux-flash'
import { createCourseAttempt } from '../../course-catalog/data'
import { getLearningHubApiErrorMessage } from '../../utils/api-error-message'
import LearningHubCourseBookmarkButton from '../../components/learning-hub-course-bookmark-button'
import { Spinner } from 'lp-components'

const INITIAL_VISIBLE_ROWS = 2
const LOAD_MORE_ROWS = 3

function getGridColumns() {
  if (typeof window === 'undefined') return 4
  const w = window.innerWidth
  if (w >= 1200) return 4
  if (w >= 1024) return 3
  if (w >= 768) return 2
  return 1
}

function useGridColumns() {
  const [columns, setColumns] = useState(getGridColumns)

  useEffect(() => {
    const onResize = () => setColumns(getGridColumns())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return columns
}

function hasCourseCertificate(course) {
  const raw = course?.certification
  return Boolean(raw && String(raw).trim())
}

function useVisibleRowCount(totalCourses, columns) {
  const maxRows = totalCourses ? Math.ceil(totalCourses / columns) : 0
  const [visibleRows, setVisibleRows] = useState(INITIAL_VISIBLE_ROWS)

  useEffect(() => {
    setVisibleRows(INITIAL_VISIBLE_ROWS)
  }, [totalCourses, columns])

  const cappedRows = Math.min(
    Math.max(visibleRows, INITIAL_VISIBLE_ROWS),
    maxRows || INITIAL_VISIBLE_ROWS
  )
  const visibleCount = Math.min(totalCourses, cappedRows * columns)
  const hasMore = visibleCount < totalCourses

  const loadMore = useCallback(() => {
    setVisibleRows((rows) => Math.min(rows + LOAD_MORE_ROWS, maxRows))
  }, [maxRows])

  return { visibleCount, hasMore, loadMore }
}

const propTypes = {
  myCourses: PropTypes.array,
  bookmarkedCourses: PropTypes.array,
  fetchLearningHubMyCourses: PropTypes.func.isRequired,
  startLearningHubCourse: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
}

const defaultProps = {
  myCourses: undefined,
  bookmarkedCourses: [],
}

function MyCoursesPage({
  myCourses,
  bookmarkedCourses,
  fetchLearningHubMyCourses,
  startLearningHubCourse,
  flashErrorMessage,
}) {
  const columns = useGridColumns()

  useEffect(() => {
    if (myCourses === undefined) fetchLearningHubMyCourses()
  }, [myCourses, fetchLearningHubMyCourses])

  const courses = myCourses || []
  const bookmarkedList = bookmarkedCourses || []
  const activeList = courses.filter(
    ({ attempt }) => attempt?.status === 'in_progress'
  )
  const completedList = courses.filter(
    ({ attempt }) => attempt?.status === 'completed'
  )
  const completedCount = completedList.length
  const inProgressCount = activeList.length

  const startedCourseIds = useMemo(
    () =>
      new Set(
        courses
          .map(({ course }) => Number(course?.id))
          .filter((id) => !Number.isNaN(id))
      ),
    [courses]
  )

  const activePaging = useVisibleRowCount(activeList.length, columns)
  const completedPaging = useVisibleRowCount(completedList.length, columns)
  const bookmarkedPaging = useVisibleRowCount(bookmarkedList.length, columns)

  const activeSlice = activeList.slice(0, activePaging.visibleCount)
  const completedSlice = completedList.slice(0, completedPaging.visibleCount)
  const bookmarkedSlice = bookmarkedList.slice(0, bookmarkedPaging.visibleCount)

  const goToCoursePlayer = useCallback(
    async (courseId, alreadyEnrolled) => {
      if (alreadyEnrolled) {
        try {
          await startLearningHubCourse(courseId)
        } catch (error) {
          flashErrorMessage(
            getLearningHubApiErrorMessage(
              error,
              'Unable to resume this course.'
            )
          )
          return
        }
        createCourseAttempt(courseId)
        browserHistory.push(`${LEARNING_HUB_ROUTE}/course/${courseId}/chapters`)
        return
      }
      try {
        await startLearningHubCourse(courseId)
        createCourseAttempt(courseId)
        await fetchLearningHubMyCourses()
        browserHistory.push(`${LEARNING_HUB_ROUTE}/course/${courseId}/chapters`)
      } catch (error) {
        flashErrorMessage(
          getLearningHubApiErrorMessage(error, 'Unable to start this course.')
        )
      }
    },
    [startLearningHubCourse, fetchLearningHubMyCourses, flashErrorMessage]
  )

  if (myCourses === undefined) {
    return (
      <>
        <header className="learning-hub-my-courses__page-header">
          <h1>My Courses</h1>
        </header>
        <Spinner />
      </>
    )
  }

  const hasAnyCourses =
    activeList.length > 0 ||
    completedList.length > 0 ||
    bookmarkedList.length > 0

  const hasEnrolledCourses = activeList.length > 0 || completedList.length > 0

  return (
    <>
      <header className="learning-hub-my-courses__page-header">
        <h1>My Courses</h1>
      </header>

      <section className="learning-hub-my-courses__stats" aria-label="My stats">
        <h2 className="learning-hub-my-courses__section-title learning-hub-my-courses__section-title--large">
          My Stats
        </h2>
        <div className="learning-hub-my-courses__stats-tiles">
          <div className="learning-hub-my-courses__stat-tile">
            <p className="learning-hub-my-courses__stat-label">
              Courses Completed
            </p>
            <p className="learning-hub-my-courses__stat-value">
              {completedCount} {completedCount === 1 ? 'course' : 'courses'}
            </p>
          </div>
          <div className="learning-hub-my-courses__stat-tile">
            <p className="learning-hub-my-courses__stat-label">
              Courses In Progress
            </p>
            <p className="learning-hub-my-courses__stat-value">
              {inProgressCount} {inProgressCount === 1 ? 'course' : 'courses'}
            </p>
          </div>
        </div>
      </section>

      <section
        className="learning-hub-my-courses__section"
        aria-labelledby="bookmarked-courses-heading"
      >
        <h2
          id="bookmarked-courses-heading"
          className="learning-hub-my-courses__section-title learning-hub-my-courses__section-title--large"
        >
          Bookmarked Courses
        </h2>
        {bookmarkedList.length > 0 ? (
          <p className="learning-hub-my-courses__section-subtitle">
            Courses you&apos;ve saved for later
          </p>
        ) : null}

        {bookmarkedList.length === 0 ? (
          <p className="learning-hub-my-courses__empty-section">
            No bookmarked courses yet. Bookmark courses from the Course Catalog
            to find them here.
          </p>
        ) : (
          <>
            <div className="learning-hub-course-catalog learning-hub-my-courses__card-area">
              <div className="learning-hub-my-courses__card-grid">
                {bookmarkedSlice.map((entry) => {
                  const course = entry.course
                  const alreadyEnrolled = startedCourseIds.has(
                    Number(course.id)
                  )
                  return (
                    <Card
                      key={course.id}
                      className="learning-hub-course-card-shell"
                    >
                      <article className="learning-hub-course-card learning-hub-my-courses__course-card">
                        <Link
                          to={`${LEARNING_HUB_ROUTE}/course/${course.id}`}
                          className="learning-hub-course-card__course-link"
                        >
                          <img
                            src={course.coverImage}
                            alt={`${course.title} cover`}
                            className="learning-hub-course-card__cover"
                          />
                          <h4>{course.title}</h4>
                          <p className="learning-hub-course-card__instructor">
                            {course.instructor}
                          </p>
                        </Link>
                        <div className="learning-hub-course-card__actions learning-hub-my-courses__bookmarked-actions">
                          <button
                            type="button"
                            className="button-primary button-small learning-hub-my-courses__primary-button"
                            onClick={() =>
                              goToCoursePlayer(course.id, alreadyEnrolled)
                            }
                          >
                            {alreadyEnrolled ? 'Continue' : 'Take Course'}
                          </button>
                          <LearningHubCourseBookmarkButton
                            courseId={course.id}
                            courseTitle={course.title}
                          />
                        </div>
                      </article>
                    </Card>
                  )
                })}
              </div>
            </div>
            {bookmarkedPaging.hasMore && (
              <div className="learning-hub-my-courses__see-more-wrap">
                <button
                  type="button"
                  className="learning-hub-my-courses__see-more"
                  onClick={bookmarkedPaging.loadMore}
                >
                  See More
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {!hasAnyCourses && (
        <Card className="learning-hub-my-courses__empty">
          <p>
            You have no courses in progress or completed yet. Start learning
            from the Course Catalog.
          </p>
          <Link
            to={`${LEARNING_HUB_ROUTE}/course-catalog`}
            className="button-primary button-small learning-hub-my-courses__primary-button"
          >
            Browse Course Catalog
          </Link>
        </Card>
      )}

      {hasEnrolledCourses && (
        <>
          <section
            className="learning-hub-my-courses__section"
            aria-labelledby="my-active-courses-heading"
          >
            <h2
              id="my-active-courses-heading"
              className="learning-hub-my-courses__section-title learning-hub-my-courses__section-title--large"
            >
              My Active Courses
            </h2>
            <p className="learning-hub-my-courses__section-subtitle">
              Here&apos;s what you&apos;re currently learning
            </p>

            {activeList.length === 0 ? (
              <p className="learning-hub-my-courses__empty-section">
                No courses in progress.
              </p>
            ) : (
              <>
                <div className="learning-hub-course-catalog learning-hub-my-courses__card-area">
                  <div className="learning-hub-my-courses__card-grid">
                    {activeSlice.map(({ course, attempt }) => (
                      <Card
                        key={course.id}
                        className="learning-hub-course-card-shell"
                      >
                        <article className="learning-hub-course-card learning-hub-my-courses__course-card">
                          <img
                            src={course.coverImage}
                            alt={`${course.title} cover`}
                            className="learning-hub-course-card__cover"
                          />
                          <h4>{course.title}</h4>
                          <p className="learning-hub-course-card__instructor">
                            {course.instructor}
                          </p>
                          <div className="learning-hub-my-courses__course-card-bottom">
                            <p className="learning-hub-my-courses__progress-label">
                              {attempt.progressPercent}% Complete
                            </p>
                            <div
                              className="learning-hub-my-courses__progress-track"
                              role="progressbar"
                              aria-valuenow={attempt.progressPercent}
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-label={`${course.title} progress`}
                            >
                              <div
                                className="learning-hub-my-courses__progress-fill"
                                style={{
                                  width: `${attempt.progressPercent}%`,
                                }}
                              />
                            </div>
                            <div className="learning-hub-course-card__actions">
                              <button
                                type="button"
                                className="button-primary button-small learning-hub-my-courses__primary-button"
                                onClick={() => {
                                  browserHistory.push(
                                    `${LEARNING_HUB_ROUTE}/course/${course.id}/chapters`
                                  )
                                }}
                              >
                                Continue
                              </button>
                            </div>
                          </div>
                        </article>
                      </Card>
                    ))}
                  </div>
                </div>
                {activePaging.hasMore && (
                  <div className="learning-hub-my-courses__see-more-wrap">
                    <button
                      type="button"
                      className="learning-hub-my-courses__see-more"
                      onClick={activePaging.loadMore}
                    >
                      See More
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          <section
            className="learning-hub-my-courses__section"
            aria-labelledby="completed-courses-heading"
          >
            <h2
              id="completed-courses-heading"
              className="learning-hub-my-courses__section-title learning-hub-my-courses__section-title--large"
            >
              Completed Courses
            </h2>
            {completedList.length > 0 && (
              <p className="learning-hub-my-courses__section-subtitle">
                You&apos;ve finished these. Great job!
              </p>
            )}

            {completedList.length === 0 ? (
              <p className="learning-hub-my-courses__empty-section">
                No completed courses yet.
              </p>
            ) : (
              <>
                <div className="learning-hub-course-catalog learning-hub-my-courses__card-area">
                  <div className="learning-hub-my-courses__card-grid">
                    {completedSlice.map(({ course }) => (
                      <Card
                        key={course.id}
                        className="learning-hub-course-card-shell"
                      >
                        <article className="learning-hub-course-card learning-hub-my-courses__course-card">
                          <Link
                            to={`${LEARNING_HUB_ROUTE}/course/${course.id}/chapters`}
                            className="learning-hub-course-card__course-link"
                          >
                            <img
                              src={course.coverImage}
                              alt={`${course.title} cover`}
                              className="learning-hub-course-card__cover"
                            />
                            <h4>{course.title}</h4>
                            <p className="learning-hub-course-card__instructor">
                              {course.instructor}
                            </p>
                          </Link>
                          {hasCourseCertificate(course) ? (
                            <div className="learning-hub-course-card__actions">
                              <button
                                type="button"
                                className="button-primary button-small learning-hub-my-courses__primary-button"
                                onClick={() => {
                                  browserHistory.push(
                                    courseCompletionPath(course.id)
                                  )
                                }}
                              >
                                View Certificate
                              </button>
                            </div>
                          ) : null}
                        </article>
                      </Card>
                    ))}
                  </div>
                </div>
                {completedPaging.hasMore && (
                  <div className="learning-hub-my-courses__see-more-wrap">
                    <button
                      type="button"
                      className="learning-hub-my-courses__see-more"
                      onClick={completedPaging.loadMore}
                    >
                      See More
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}
    </>
  )
}

MyCoursesPage.propTypes = propTypes
MyCoursesPage.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    myCourses: globalSelectors.myCourses(state),
    bookmarkedCourses: globalSelectors.bookmarkedCourses(state),
  }
}

const mapDispatchToProps = {
  fetchLearningHubMyCourses: apiActions.fetchLearningHubMyCourses,
  startLearningHubCourse: apiActions.startLearningHubCourse,
  flashErrorMessage: flashActions.flashErrorMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  MyCoursesPage
)
